/* eslint-disable camelcase */
import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  withStyles,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useConfirm } from 'material-ui-confirm'
import Icon from '@mdi/react'
import {
  mdiDelete,
  mdiDragVertical,
  mdiNoteEditOutline,
  mdiPlus,
} from '@mdi/js'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import colors from '../../../../assets/colors'
import filterStylesComponents from '../../../../assets/filterComponentsStyles'
import { StyledTableCellHeader } from '../../../../components/hooks/BaseTableComponents'
import { CustomSwitch } from '../../../../components/ui/custom-switch'
import { usePermissions } from '../../../../hooks/usePermission'
import {
  deleteVisitChecklistQuestion,
  fetchVisitChecklistQuestions,
  reorderVisitChecklistQuestions,
  storeVisitChecklistQuestion,
  updateVisitChecklistQuestion,
} from '../../VisitChecklistActions'

import QuestionModal from './QuestionModal'

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#FFFF',
    color: colors.textPrimary,
  },
  body: {
    fontSize: 14,
    padding: '0 12px',
    color: colors.textPrimary,
  },
}))(TableCell)

const TYPE_LABELS = {
  boolean: 'Sim/Não',
  text: 'Texto',
}

const useStyles = makeStyles({
  inactiveRow: {
    opacity: 0.6,
  },
  emptyMessage: {
    padding: '24px 0',
    textAlign: 'center',
    color: colors.textGray,
  },
})

const DragHandleContext = createContext(null)

const SortableRow = ({ id, children, className }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <DragHandleContext.Provider value={{ attributes, listeners }}>
      <TableRow hover ref={setNodeRef} style={style} className={className}>
        {children}
      </TableRow>
    </DragHandleContext.Provider>
  )
}

const DragHandle = ({ disabled }) => {
  const { attributes, listeners } = useContext(DragHandleContext)

  return (
    <StyledTableCell align="center" style={{ width: '40px' }}>
      <Box
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'default' : 'grab',
          color: '#bdbdbd',
          touchAction: 'none',
        }}
      >
        <Icon path={mdiDragVertical} size={1} />
      </Box>
    </StyledTableCell>
  )
}

export const VisitChecklist = () => {
  const classes = useStyles()
  const styles = filterStylesComponents()
  const dispatch = useDispatch()
  const confirm = useConfirm()

  const questions = useSelector(state => state.configs.visitChecklistQuestions)
  const permissionUser = useSelector(state => state.auth.permissionUser)

  const { canCreate, canUpdate, canDelete } = usePermissions()

  const [items, setItems] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [question, setQuestion] = useState(null)

  const allowCreate = canCreate('checklist-visita', permissionUser)
  const allowUpdate = canUpdate('checklist-visita', permissionUser)
  const allowDelete = canDelete('checklist-visita', permissionUser)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  useEffect(() => {
    dispatch(fetchVisitChecklistQuestions())
  }, [])

  useEffect(() => {
    setItems(questions ?? [])
  }, [questions])

  const handleOpenModal = (selected = null) => {
    setQuestion(selected)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setQuestion(null)
  }

  const handleSubmit = async values => {
    const resp = question?.id
      ? await dispatch(updateVisitChecklistQuestion(question.id, values))
      : await dispatch(storeVisitChecklistQuestion(values))

    if (!resp?.data) return

    handleCloseModal()
    dispatch(fetchVisitChecklistQuestions())
  }

  const handleToggleStatus = async (selected, status) => {
    const resp = await dispatch(
      updateVisitChecklistQuestion(selected?.id, { status })
    )

    if (resp?.data) dispatch(fetchVisitChecklistQuestions())
  }

  const handleDelete = selected => {
    confirm({
      title: 'Tem certeza?',
      description: `Deseja excluir a pergunta "${selected?.label}"? As visitas já respondidas mantêm o histórico.`,
      confirmationText: 'Sim',
      cancellationText: 'Cancelar',
      dialogProps: { fullWidth: true },
    }).then(async () => {
      const resp = await dispatch(deleteVisitChecklistQuestion(selected?.id))

      if (resp?.data) dispatch(fetchVisitChecklistQuestions())
    })
  }

  const handleDragEnd = async event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = items.findIndex(
      item => String(item.id) === String(active.id)
    )
    const newIndex = items.findIndex(
      item => String(item.id) === String(over.id)
    )
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove([...items], oldIndex, newIndex)
    setItems(reordered)

    const resp = await dispatch(
      reorderVisitChecklistQuestions(reordered.map(item => item.id))
    )

    // falhou o reorder: volta para a ordem que veio da api
    if (!resp?.data) setItems(questions ?? [])
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography style={{ color: colors.textGray }}>
              Perguntas que o técnico responde na visita. Arraste para definir a
              ordem de exibição no app.
            </Typography>
          </Grid>

          {allowCreate && (
            <Grid item>
              <Button
                type="button"
                className={styles.btnAdd}
                onClick={() => handleOpenModal()}
              >
                <Icon path={mdiPlus} size={0.9} />
                Nova Pergunta
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCellHeader align="center" />
                  <StyledTableCellHeader align="center">
                    Ordem
                  </StyledTableCellHeader>
                  <StyledTableCellHeader align="start">
                    Pergunta
                  </StyledTableCellHeader>
                  <StyledTableCellHeader align="center">
                    Tipo
                  </StyledTableCellHeader>
                  <StyledTableCellHeader align="center">
                    Obrigatória
                  </StyledTableCellHeader>
                  <StyledTableCellHeader align="center">
                    Ativa
                  </StyledTableCellHeader>
                  <StyledTableCellHeader align="center">
                    Ações
                  </StyledTableCellHeader>
                </TableRow>
              </TableHead>

              <SortableContext
                items={items.map(item => String(item.id))}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {items.map((item, index) => (
                    <SortableRow
                      key={item.id}
                      id={String(item.id)}
                      className={item?.status ? '' : classes.inactiveRow}
                    >
                      <DragHandle disabled={!allowUpdate} />

                      <StyledTableCell align="center">
                        {index + 1}
                      </StyledTableCell>

                      <StyledTableCell align="start">
                        {item?.label}
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        {TYPE_LABELS[item?.type] ?? item?.type}
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        {item?.is_required ? 'Sim' : 'Não'}
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        <CustomSwitch
                          checked={!!item?.status}
                          disabled={!allowUpdate}
                          onChange={event =>
                            handleToggleStatus(item, event.target.checked)
                          }
                        />
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{ gap: '4px' }}
                        >
                          {allowUpdate && (
                            <IconButton
                              size="small"
                              title="Editar"
                              onClick={() => handleOpenModal(item)}
                            >
                              <Icon
                                path={mdiNoteEditOutline}
                                size={0.9}
                                color={colors.primary}
                              />
                            </IconButton>
                          )}

                          {allowDelete && (
                            <IconButton
                              size="small"
                              title="Excluir"
                              onClick={() => handleDelete(item)}
                            >
                              <Icon
                                path={mdiDelete}
                                size={0.9}
                                color={colors.primary}
                              />
                            </IconButton>
                          )}
                        </Box>
                      </StyledTableCell>
                    </SortableRow>
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </TableContainer>

          {!items?.length && (
            <Typography className={classes.emptyMessage}>
              Nenhuma pergunta cadastrada no checklist da visita.
            </Typography>
          )}
        </DndContext>
      </Grid>

      <QuestionModal
        open={modalOpen}
        question={question}
        handleClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </Grid>
  )
}
